import { Dispatch, Fragment, SetStateAction, useCallback, useEffect, useState } from 'react';
import PageContent from 'components/PageContent';
import Countdown from 'components/CountDown';
import { IFlightTicketProposal } from 'entities/Flights/sdk.ts';
import { useTranslation } from 'react-i18next';
import Modal from 'components/Modal';
import { Field, FieldGroup, FieldLabel, FieldDescription } from 'components/ui/field';
import { Input } from 'components/ui/input';
import TotalSection from 'pages/FlightOffer/TotalSection';
import { FlightOfferViewEnum } from 'pages/FlightOffer/constants.tsx';
import { KeyedMutator } from 'swr';
import { useAuth } from 'entities/Auth';
import * as yup from 'yup';
import { t } from 'i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router';
import { sendVerificationEmail } from 'api/InvoiceDetails/sdk.ts';
import { H2, H4 } from 'components/ui/typography.tsx';
import { Button } from 'components/ui/button.tsx';
import { cn } from 'utils/utils';
import { UserIcon } from '@heroicons/react/20/solid';
import { emailAtom } from 'pages/FlightOffer/EmailAtom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useAtom } from 'jotai';
import { useOutletContext } from 'react-router';

interface InvoiceDetailsViewProps {
  flightTicketProposal: IFlightTicketProposal;
  currency: string | null;
  setCurrency: Dispatch<SetStateAction<string | null>>;
  flightTicketProposalMutate: KeyedMutator<IFlightTicketProposal>;
}

const schema = yup.object().shape({
  email: yup
    .string()
    .email(() => t('common.invalidEmailLabel'))
    .required(() => t('common.emailIsRequiredLabel')),
});

interface EmailViewForm {
  email: string;
}

export const InvoiceDetailsEmailView = () => {
  const { flightTicketProposal, currency, setCurrency, flightTicketProposalMutate } =
    useOutletContext<InvoiceDetailsViewProps>();
  const { t } = useTranslation();
  const [isExpiredModalOpen, setIsExpiredModalOpen] = useState(false);
  const [isVerificationEmailSent, setIsVerificationEmailSent] = useState<boolean>(false);
  const [isSendingVerificationEmail, setIsSendingVerificationEmail] = useState<boolean>(false);
  const [verificationEmail, setVerificationEmail] = useAtom(emailAtom);
  const params = useParams<{ flightTicketRequestUUID: string }>();
  const isOfferExpired = new Date(flightTicketProposal.expiration_datetime) < new Date();
  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const {
    formState: { isValid, errors, touchedFields },
    getValues,
    register,
    reset,
  } = useForm<EmailViewForm>({
    defaultValues: { email: '' },
    resolver: yupResolver(schema),
    mode: 'onTouched',
  });

  const handleRefreshClick = useCallback(() => {
    window.location.reload();
  }, []);

  const handleCountdownExpire = useCallback(() => {
    setIsExpiredModalOpen(true);
  }, []);

  useEffect(() => {
    if (userInfo?.email) {
      reset({ email: userInfo.email }, { keepTouched: false });
    }
  }, [userInfo?.email, reset]);

  const handleConfirmAndProceedClick = useCallback(async () => {
    if (isOfferExpired || isSendingVerificationEmail) {
      return;
    }
    const emailValue = getValues('email').trim();
    setVerificationEmail(emailValue);

    if (emailValue === userInfo?.email) {
      navigate(`/flights/offer/${params.flightTicketRequestUUID}/invoice-details`);
      return;
    }

    try {
      setIsSendingVerificationEmail(true);

      await sendVerificationEmail(params.flightTicketRequestUUID!, emailValue);

      setIsVerificationEmailSent(true);
    } finally {
      setIsSendingVerificationEmail(false);
    }
  }, [
    isOfferExpired,
    isSendingVerificationEmail,
    getValues,
    setVerificationEmail,
    userInfo?.email,
    navigate,
    params.flightTicketRequestUUID,
  ]);

  return (
    <Fragment>
      <PageContent>
        <div className='flex justify-between md:items-center flex-col  md:flex-row'>
          <H2>{t('invoiceDetails.invoiceDetails')}</H2>
          <span className='font-[MuseoSansCyrl700] flex md:text-lg md:flex-col max-[300px]:flex-col'>
            <span>{t('flightOffer.offerExpiresIn')}:&nbsp;</span>
            <Countdown
              targetTs={new Date(flightTicketProposal.expiration_datetime).getTime()}
              onExpire={handleCountdownExpire}
            />
          </span>
        </div>
        <div className='w-full bg-lightBeige mt-10 p-10 h-[400px] rounded-lg'>
          {isVerificationEmailSent ? (
            <div className='flex flex-row gap-10 items-center h-full justify-center'>
              <CheckCircleIcon
                aria-hidden='true'
                className='w-[123px] h-[123px] max-lg:w-[64px] max-lg:h-[64px] text-content-primary'
              />
              <p>
                {t('invoiceDetails.verificationLink', { emailAddress: verificationEmail })}
                <span className='text-blue-500 cursor-pointer' onClick={() => setIsVerificationEmailSent(false)}>
                  {' '}
                  {t('invoiceDetails.here')}
                </span>
              </p>
            </div>
          ) : (
            <FieldGroup>
              <div className='flex flex-row max-md:flex-col justify-between gap-10'>
                <div className='flex flex-col gap-8'>
                  <div className='flex flex-row gap-2 items-center'>
                    <UserIcon className='h-6 w-6 text-content-secondary' />
                    <H4 className={cn('font-[MuseoSansCyrl500]')}>{t('common.emailLabel')}</H4>
                  </div>
                  <Field>
                    <FieldLabel htmlFor='email'>{t('common.emailLabel')}</FieldLabel>
                    <Input
                      isTouched={touchedFields.email}
                      error={errors.email}
                      {...register('email')}
                      id='fieldgroup-name'
                      type='text'
                      placeholder='user@example.com'
                    />
                    <FieldDescription className={cn('font-[MuseoSansCyrl500] text-content-secondary')}>
                      {t('invoiceDetails.sendInvoiceToEmail')}
                    </FieldDescription>
                  </Field>
                </div>
              </div>
            </FieldGroup>
          )}
        </div>
      </PageContent>
      <TotalSection
        view={FlightOfferViewEnum.INVOICE_DETAILS_EMAIL}
        isConfirmDisabled={!isValid || isVerificationEmailSent || isSendingVerificationEmail}
        isConfirmLoading={isSendingVerificationEmail}
        flightTicketProposal={flightTicketProposal}
        handleConfirmClick={handleConfirmAndProceedClick}
        currency={currency}
        setCurrency={setCurrency}
        flightTicketProposalMutate={flightTicketProposalMutate}
        backButtonClick={() => navigate(`/flights/offer/${params.flightTicketRequestUUID}`)}
      />
      <Modal
        open={isExpiredModalOpen}
        title={t('flightOffer.offerHasExpired')}
        content={
          <div className='w-full'>
            <div className='flex justify-center'>
              <Button onClick={handleRefreshClick}>{t('common.refresh')}</Button>
            </div>
          </div>
        }
      ></Modal>
    </Fragment>
  );
};
